<template>
  <el-dialog
    :visible.sync="visible"
    :title="dialogTitle"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    custom-class="word-review-dialog"
    @closed="handleClosed"
  >
    <!-- 对话框头部控制 -->
    <div class="dialog-header" slot="title">
      <div class="header-info">
        <i :class="modeIcon"></i>
        <span>{{ dialogTitle }}</span>
        <el-tag size="small" :type="modeType">{{ modeText }}</el-tag>
      </div>
      <div class="header-controls">
        <el-button
          @click="toggleBackgroundMusic"
          :type="backgroundMusicPlaying ? 'primary' : 'default'"
          size="mini"
          icon="el-icon-headset"
        >
          {{ backgroundMusicPlaying ? "音乐已开" : "背景音乐" }}
        </el-button>
        <el-button @click="closeDialog" size="mini" icon="el-icon-close">
          关闭
        </el-button>
      </div>
    </div>


    <!-- 初始开始页面 -->
    <div v-if="currentStep === 'start'" class="start-content">
      <div class="stage-info" v-if="selectedStage">
        <div class="stage-header">
          <i :class="selectedStage.icon"></i>
          <h3>{{ selectedStage.name }}</h3>
        </div>
        <p>{{ selectedStage.description }}</p>
      </div>

      <div class="review-summary">
        <div class="summary-item">
          <div class="item-icon">
            <i class="el-icon-document"></i>
          </div>
          <div class="item-content">
            <div class="number">{{ wordsList.length }}</div>
            <div class="label">
              {{ mode === "review" ? "需要复习" : "新单词" }}
            </div>
          </div>
        </div>

        <div class="summary-item" v-if="mode === 'review' && overdueCount > 0">
          <div class="item-icon warning">
            <i class="el-icon-warning"></i>
          </div>
          <div class="item-content">
            <div class="number">{{ overdueCount }}</div>
            <div class="label">过期单词</div>
          </div>
        </div>
      </div>

      <div class="start-tips">
        <h4>
          <i class="el-icon-info"></i>
          {{ mode === "review" ? "复习提示" : "学习提示" }}
        </h4>
        <ul>
          <li v-if="mode === 'review'">认真回忆单词含义，巩固记忆</li>
          <li v-else>专注学习新单词，建立初始印象</li>
          <li>遇到困难时可以查看提示</li>
          <li>建议在安静环境下学习，效果更好</li>
        </ul>
      </div>

      <div class="start-actions">
        <el-button
          type="primary"
          size="large"
          @click="startLearning"
          :loading="loading"
        >
          <i class="el-icon-play-arrow"></i>
          {{ mode === "review" ? "开始复习" : "开始学习" }}
        </el-button>
      </div>
    </div>

    <!-- 学习/复习内容 -->
    <div v-else-if="currentStep === 'learning'" class="learning-content">
      <!-- 学习进度 -->
      <div class="learning-progress">
        <div class="progress-info">
          <span class="current-phase">{{ getPhaseText() }}</span>
          <span class="words-count">{{ currentIndex + 1 }} / {{ totalWords }} 个单词</span>
        </div>
        <el-progress 
          :percentage="progressPercentage" 
          :stroke-width="8"
          :show-text="false"
        ></el-progress>
      </div>

      <!-- 第一阶段开始页面 -->
      <div class="phase-start" v-if="currentPhase === 'phase1-start'">
        <div class="start-content">
          <h2>第一阶段：英文→中文</h2>
          <p>看到英文单词，回忆中文意思</p>
          <p>点击"记住"或"没记住"来标记你的掌握程度</p>
          <el-button type="primary" size="large" @click="startPhase1">
            开始学习
          </el-button>
        </div>
      </div>

      <!-- 第一阶段：英文→中文 -->
      <div class="phase-content" v-if="currentPhase === 'phase1'">
        <div class="phase-word-card">
          <div class="phase-word-header">
            <span class="phase-title">第一阶段：英文→中文</span>
          </div>
          
          <div class="phase-word-content">
            <div class="phase-word-top">
              <!-- 图片区域 - 固定在左侧红框位置 -->
              <div class="phase-word-image" v-if="currentWord && (currentWord._resolved_image_url || currentWord.image_url)">
                <img 
                  :src="currentWord._resolved_image_url || getImageUrl(currentWord.image_url)" 
                  :alt="currentWord.word || currentWord.english_word"
                  class="phase-word-img"
                  @error="handleImageError"
                  @load="handleImageLoad"
                />
              </div>
              
              <!-- 单词区域 - 保持原有布局 -->
              <div class="phase-word-main">
                <h3 class="phase-english-word">{{ currentWord.word || currentWord.english_word }}</h3>
                <div class="phase-word-phonetic">
                  <span v-if="currentWord.phonetic_en" class="phase-phonetic-item">英[{{ currentWord.phonetic_en }}]</span>
                  <span v-if="currentWord.phonetic_us" class="phase-phonetic-item">美[{{ currentWord.phonetic_us }}]</span>
                </div>
              </div>
            </div>

            <div class="phase-word-actions">
              <el-button 
                type="success" 
                size="large" 
                @click="markAsKnown"
                icon="el-icon-check"
              >
                记住
              </el-button>
              <el-button 
                type="warning" 
                size="large" 
                @click="markAsUnknown"
                icon="el-icon-close"
              >
                没记住
              </el-button>
              <el-button 
                type="info" 
                size="small" 
                @click="playPronunciation"
                icon="el-icon-video-play"
              >
                发音
              </el-button>
            </div>
            
            <div class="phase-word-meaning" v-if="showMeaning">
              <p class="phase-chinese-meaning">{{ currentWord.chinese_meaning }}</p>
              <p class="phase-example-sentence" v-if="currentWord.example_sentence">
                {{ currentWord.example_sentence }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 第二阶段开始页面 -->
      <div class="phase-start" v-if="currentPhase === 'phase2-start'">
        <div class="start-content">
          <h2>第二阶段：中文→英文</h2>
          <p>看到中文释义，回忆英文单词</p>
          <p>点击"记住"或"没记住"来标记你的掌握程度</p>
          <el-button type="primary" size="large" @click="startPhase2">
            开始学习
          </el-button>
        </div>
      </div>

      <!-- 第二阶段：中文→英文 -->
      <div class="phase-content" v-if="currentPhase === 'phase2'">
        <div class="phase-word-card">
          <div class="phase-word-header">
            <span class="phase-title">第二阶段：中文→英文</span>
          </div>
          
          <div class="phase-word-content">
            <!-- <div class="phase-word-top"> -->
              <!-- 图片区域 - 固定在左侧红框位置 -->
              <!-- <div class="phase-word-image" v-if="currentWord.image_url">
                <img 
                  :src="getImageUrl(currentWord.image_url)" 
                  :alt="currentWord.word || currentWord.english_word"
                  class="phase-word-img"
                  @error="handleImageError"
                  @load="handleImageLoad"
                />
              </div> -->
              
              <!-- 单词区域 - 保持原有布局 -->
              <div class="phase-word-main">
                <h3 class="phase-chinese-meaning">{{ currentWord.chinese_meaning }}</h3>
                <div class="phase-word-phonetic">
                  <span v-if="currentWord.phonetic_en" class="phase-phonetic-item">英[{{ currentWord.phonetic_en }}]</span>
                  <span v-if="currentWord.phonetic_us" class="phase-phonetic-item">美[{{ currentWord.phonetic_us }}]</span>
                </div>
              </div>

            <!-- </div> -->
            
            <div class="phase-word-actions">
              <el-button 
                type="success" 
                size="large" 
                @click="markAsKnown"
                icon="el-icon-check"
              >
                记住
              </el-button>
              <el-button 
                type="warning" 
                size="large" 
                @click="markAsUnknown"
                icon="el-icon-close"
              >
                没记住
              </el-button>
              <el-button 
                type="info" 
                size="small" 
                @click="playPronunciation"
                icon="el-icon-video-play"
              >
                发音
              </el-button>
            </div>
            
            <div class="phase-word-meaning" v-if="showMeaning">
              <p class="phase-english-word">{{ currentWord.word || currentWord.english_word }}</p>
              <p class="phase-example-sentence" v-if="currentWord.example_sentence">
                {{ currentWord.example_sentence }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 第三阶段开始页面 -->
      <div class="phase-start" v-if="currentPhase === 'phase3-start'">
        <div class="start-content">
          <h2>第三阶段：拼写测试</h2>
          <p>看到中文释义，输入英文单词</p>
          <p>必须完成所有单词的拼写测试，不能跳过</p>
          <el-button type="primary" size="large" @click="startPhase3">
            开始测试
          </el-button>
        </div>
      </div>

      <!-- 第三阶段：拼写测试 -->
      <div class="phase-content" v-if="currentPhase === 'phase3'">
        <div class="phase-word-card">
          <div class="phase-word-header">
            <span class="phase-title">第三阶段：拼写测试</span>
          </div>
          
          <div class="phase-word-content">
            <div class="phase-word-main">
              <h3 class="phase-chinese-meaning">{{ currentWord.chinese_meaning }}</h3>
               <div class="phase-word-phonetic">
                <span v-if="currentWord.phonetic_en" class="phase-phonetic-item">英[{{ currentWord.phonetic_en }}]</span>
                <span v-if="currentWord.phonetic_us" class="phase-phonetic-item">美[{{ currentWord.phonetic_us }}]</span>
              </div>
            </div>
            
            <div class="phase-spelling-input">
              <el-input
                ref="spellingInput"
                v-model="userInput"
                placeholder="请输入英文单词"
                size="large"
                @keyup.enter="checkSpelling"
                @keydown.enter.native="checkSpelling"
                :disabled="showAnswer"
              ></el-input>
              <el-button 
                type="primary" 
                size="large" 
                @click="checkSpelling"
                :disabled="!userInput.trim() || showAnswer"
              >
                检查
              </el-button>
            </div>
            
            <div class="phase-word-actions">
              <el-button 
                type="info" 
                size="small" 
                @click="playPronunciation"
                icon="el-icon-video-play"
              >
                发音
              </el-button>
            </div>
            
            <div class="phase-word-meaning" v-if="showAnswer">
              <p class="phase-english-word" :class="{ 'phase-correct': isCorrect, 'phase-incorrect': !isCorrect }">
                {{ currentWord.word || currentWord.english_word }}
              </p>
              <p class="phase-example-sentence" v-if="currentWord.example_sentence">
                {{ currentWord.example_sentence }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 学习总结 -->
      <div class="learning-summary" v-if="currentPhase === 'summary'">
        <div class="summary-header">
          <h3>学习总结</h3>
          <p>今日单词学习完成</p>
        </div>
        
        <div class="summary-stats">
          <div class="stat-item">
            <span class="stat-label">单词总数</span>
            <span class="stat-value">{{ totalWords }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">认识单词</span>
            <span class="stat-value known">{{ knownWords.length }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">不认识单词</span>
            <span class="stat-value unknown">{{ unknownWords.length }}</span>
          </div>
          <div class="stat-item">
            <span class="stat-label">掌握率</span>
            <span class="stat-value" :class="{ 'excellent': masteryRate >= 90, 'good': masteryRate >= 80 && masteryRate < 90, 'poor': masteryRate < 60 }">{{ masteryRate }}%</span>
          </div>
        </div>
        
        <div class="learning-quality" v-if="unknownWords.length > 0">
          <div class="quality-warning">
            <i class="el-icon-warning"></i>
            <span>有 {{ unknownWords.length }} 个单词没记住，会影响学习效果和成绩！</span>
          </div>
        </div>
        
        <div class="summary-actions">
          <el-button 
            type="success" 
            @click="finishLearning"
            size="large"
          >
            完成学习
          </el-button>
          <el-button 
            v-if="unknownWords.length > 0"
            type="warning" 
            @click="reviewUnknownWords"
            size="large"
          >
            重新学习没记住的单词 ({{ unknownWords.length }}个)
          </el-button>
        </div>
      </div>
    </div>

    <!-- 完成页面 -->
    <div v-else-if="currentStep === 'complete'" class="complete-content">
      <div class="complete-icon">
        <i class="el-icon-circle-check"></i>
      </div>
      <h3>{{ mode === "review" ? "复习完成！" : "学习完成！" }}</h3>

      <div class="complete-stats">
        <div class="stat-item">
          <div class="stat-number">{{ answeredCount }}</div>
          <div class="stat-label">总计单词</div>
        </div>
        <div class="stat-item">
          <div class="stat-number">{{ correctCount }}</div>
          <div class="stat-label">掌握单词</div>
        </div>
      </div>

      <div class="complete-actions">
        <el-button @click="restartSession" type="primary">
          <i class="el-icon-refresh"></i>
          再来一轮
        </el-button>
        <el-button @click="closeDialog">
          <i class="el-icon-close"></i>
          关闭
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import ProgressAPI from "@/api/progress";
import { homeApi } from "@/api/home";
import { mapGetters } from "vuex";

export default {
  name: "WordReviewDialog",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "review", // 'review' 或 'learn'
      validator: (value) => ["review", "learn"].includes(value),
    },
    selectedStage: {
      type: Object,
      default: null,
    },
    initialWords: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      visible: this.value,
      currentStep: "start", // 'start', 'learning', 'complete'
      currentIndex: 0,
      currentWord: {},
      wordsList: [],
      showMeaning: false,
      showPhonetic: false,
      loading: false,

      // 统计数据
      correctCount: 0,
      answeredCount: 0,
      startTime: null,
      studyTime: 0,

      // 用户统计信息
      userStats: null,

      // 音频控制
      backgroundMusicPlaying: false,
      backgroundMusic: null,
      currentAudio: null,
      errorAudio: null,
      
      // 三阶段学习相关
      currentPhase: 'phase1-start', // phase1-start, phase1, phase2-start, phase2, phase3-start, phase3, summary
      totalWords: 0,
      originalWordCount: 0,
      userInput: '',
      showAnswer: false,
      isCorrect: false,
      isRemembered: false,
      
      // 学习结果统计
      phase1Results: [],
      phase2Results: [],
      phase3Results: [],
      knownWords: [],
      unknownWords: [],
      
      // 防止重复调用的标志
      isReviewingUnknownWords: false,
      
      // P0修复：拼写重试次数控制
      spellingRetryCount: {},  // 记录每个单词的重试次数
      maxSpellingRetries: 3,   // 最大重试次数
      
      // P0修复：重新学习次数控制
      phaseRetryCount: {       // 记录每个阶段的重试次数
        phase1: 0,
        phase2: 0,
        phase3: 0
      },
      maxPhaseRetries: 2,      // 每个阶段最多重新学习2次
      
      // P1-4修复：使用Map结构存储结果，避免数组索引越界
      phase1ResultMap: new Map(),
      phase2ResultMap: new Map(),
      phase3ResultMap: new Map(),
      
      // P1-6修复：保存音频定时器引用，方便清理
      audioTimers: [],  // 存储所有音频定时器ID
    };
  },

  computed: {
    ...mapGetters("user", ["userInfo"]),

    dialogTitle() {
      return this.mode === "review" ? "单词复习" : "学习新单词";
    },

    modeIcon() {
      return this.mode === "review" ? "el-icon-refresh" : "el-icon-plus";
    },

    modeType() {
      return this.mode === "review" ? "warning" : "success";
    },

    modeText() {
      return this.mode === "review" ? "复习模式" : "学习模式";
    },


    overdueCount() {
      // 计算真实的过期单词数量
      const now = new Date();
      return this.wordsList.filter(word => {
        return word.next_review_time && new Date(word.next_review_time) <= now;
      }).length;
    },

    userId() {
      return this.userInfo?.id || 1; // 默认用户ID，实际应该从登录状态获取
    },

    // 三阶段学习计算属性
    progressPercentage() {
      if (this.totalWords === 0) return 0;
      return Math.round(((this.currentIndex + 1) / this.totalWords) * 100);
    },
    masteryRate() {
      if (this.totalWords === 0) return 0;
      return Math.round((this.knownWords.length / this.totalWords) * 100);
    },
  },

  watch: {
    value(newVal) {
      this.visible = newVal;
      if (newVal) {
        this.initDialog();
      }
    },

    visible(newVal) {
      this.$emit("input", newVal);
    },
  },

  methods: {
    // 初始化对话框
    async initDialog() {
      this.currentStep = "start";
      this.currentIndex = 0;
      this.correctCount = 0;
      this.answeredCount = 0;
      this.studyTime = 0;
      this.showMeaning = false;
      this.showPhonetic = false;

      // 初始化三阶段学习
      this.currentPhase = 'phase1-start';
      this.totalWords = 0;
      this.originalWordCount = 0;
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.isRemembered = false;
      this.phase1Results = [];
      this.phase2Results = [];
      this.phase3Results = [];
      this.knownWords = [];
      this.unknownWords = [];
      this.isReviewingUnknownWords = false;

      // 加载用户统计信息
      await this.loadUserStats();
      
      // 加载单词列表
      await this.loadWords();
    },

    // 加载用户统计信息
    async loadUserStats() {
      try {
        const response = await ProgressAPI.getStatistics();
        if (response.success) {
          this.userStats = response.data;
          console.log("用户统计信息:", this.userStats);
        }
      } catch (error) {
        console.error("获取用户统计失败:", error);
      }
    },

    // 加载单词数据
    async loadWords() {
      this.loading = true;
      try {
        // 优先使用传入的初始单词
        if (this.initialWords.length > 0) {
          this.wordsList = [...this.initialWords];
          console.log(`使用传入的 ${this.wordsList.length} 个单词进行学习`);
        } 
        // 检查selectedStage中是否有initialWords
        else if (this.selectedStage && this.selectedStage.initialWords && this.selectedStage.initialWords.length > 0) {
          this.wordsList = [...this.selectedStage.initialWords];
          console.log(`使用阶段传入的 ${this.wordsList.length} 个单词进行学习`);
        }
        // 复习模式：调用API获取待复习单词
        else if (this.mode === "review") {
          const response = await ProgressAPI.getReviewList();
          
          if (response.success && response.data && response.data.length > 0) {
            this.wordsList = response.data;
            this.$message.success(`加载了 ${response.data.length} 个待复习单词`);
          } else {
            this.$message.info("暂无需要复习的单词");
            this.wordsList = [];
            this.closeDialog();
            return;
          }
        }

        if (this.wordsList.length > 0) {
          this.currentWord = this.wordsList[0];
          console.log("当前学习单词:", this.currentWord);
        }
        
        // 初始化三阶段学习数据
        this.totalWords = this.wordsList.length;
        this.originalWordCount = this.wordsList.length;
        
        // 初始化结果数组
        this.phase1Results = new Array(this.totalWords).fill(null);
        this.phase2Results = new Array(this.totalWords).fill(null);
        this.phase3Results = new Array(this.totalWords).fill(null);
      } catch (error) {
        console.error("加载单词失败:", error);
      } finally {
        this.loading = false;
      }
    },

    // 开始学习
    startLearning() {
      this.currentStep = "learning";
      this.startTime = Date.now();
      this.currentIndex = 0;
      this.currentWord = this.wordsList[0];
      this.showMeaning = false;
      this.showPhonetic = false;
      
      // 初始化三阶段学习
      this.currentPhase = 'phase1-start';
      this.totalWords = this.wordsList.length;
      this.originalWordCount = this.wordsList.length;
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.isRemembered = false;
      this.phase1Results = new Array(this.totalWords).fill(null);
      this.phase2Results = new Array(this.totalWords).fill(null);
      this.phase3Results = new Array(this.totalWords).fill(null);
      this.knownWords = [];
      this.unknownWords = [];
      this.isReviewingUnknownWords = false;
      
      // 自动播放背景音乐
      this.playBackgroundMusic();
    },

    // 显示含义
    revealMeaning() {
      this.showMeaning = true;
    },

    // 切换音标显示
    togglePhonetic() {
      this.showPhonetic = !this.showPhonetic;
    },

    // 显示提示
    showHint() {
      this.$message.info("提示：仔细回忆这个单词的含义");
    },

    // 获取图片完整URL
    getImageUrl(imageUrl) {
      // 如果外部预加载阶段已经写入解析字段，直接返回
      if (this.currentWord && this.currentWord._resolved_image_url) {
        return this.currentWord._resolved_image_url;
      }
      if (!imageUrl) {
        return '';
      }
      
      // 如果已经是完整URL，直接返回
      if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
        return imageUrl;
      }
      
      // 拼接API基础URL和图片路径
      const { config } = require('@/config/env');
      const baseUrl = config.VOICE_URL;
      const fullUrl = baseUrl + imageUrl;
      
      return fullUrl;
    },

    // 处理图片加载错误
    handleImageError(event) {
      console.warn('图片加载失败:', event.target.src);
      // 隐藏整个图片容器
      const imageContainer = event.target.closest('.phase-word-image');
      if (imageContainer) {
        imageContainer.style.display = 'none';
      }
    },

    // 处理图片加载成功
    handleImageLoad(event) {
      console.log('图片加载成功:', event.target.src);
    },

    // 播放发音
    playPronunciation() {
      // 播放单词发音
      const pronunciationUrl = this.currentWord.pronunciation_url;
      if (pronunciationUrl) {
        // 使用音频工具函数
        const { playAudio } = require('@/utils/audioUtils');
        playAudio(pronunciationUrl).catch(e => {
          console.error("播放音频失败:", e);
          this.$message.warning("音频播放失败");
        });
      } else {
        this.$message.info("暂无发音文件");
      }
    },

    // 自动播放两次发音（带间隔）
    autoPlayWordTwice() {
      const pronunciationUrl = this.currentWord && this.currentWord.pronunciation_url;
      if (!pronunciationUrl) {
        console.log('[AutoPlay] 当前单词没有发音URL，跳过自动播放');
        return;
      }
      const { playAudio } = require('@/utils/audioUtils');
      // 第一次播放
      playAudio(pronunciationUrl)
        .then(() => {
          // 设定一个轻微延迟再播第二次，避免音频尚未释放
          setTimeout(() => {
            playAudio(pronunciationUrl).catch(err => {
              console.warn('[AutoPlay] 第二次播放失败', err);
            });
          }, 1000); // 可调节间隔
        })
        .catch(err => {
          console.warn('[AutoPlay] 第一次播放失败', err);
        });
    },

    // 处理回答
    async handleAnswer(isCorrect) {
      this.answeredCount++;
      
      // 计算result值 (0=不认识, 1=有点模糊, 2=认识, 3=很熟悉)
      const result = this.calculateResult(isCorrect);
      
      try {
        // 提交学习结果到后端
        const response = await ProgressAPI.submitReview({
          userId: this.userId,
          wordId: this.currentWord.id,
          result: result,
          reviewType: this.mode, // 'review' 或 'learn'
          reviewTime: new Date().toISOString()
        });
        
        if (response.success) {
          // 更新本地状态
          if (isCorrect) {
            this.correctCount++;
            this.playSuccessSound();
          } else {
            this.playErrorSound();
          }
          
          // 更新单词的掌握程度和下次复习时间
          if (response.data) {
            this.currentWord.masteryLevel = response.data.masteryLevel;
            this.currentWord.nextReview = response.data.nextReview;
          }
          
          console.log(`学习结果已保存: 单词${this.currentWord.word}, 结果${result}`);
        } else {
          console.warn("保存学习结果失败:", response.message);
          // 即使保存失败，也继续学习流程
        }
      } catch (error) {
        console.error("提交学习结果失败:", error);
        this.$message.warning("保存学习记录失败，但学习继续进行");
        // 即使提交失败，也继续学习流程
      }
      
      // 移动到下一个单词
      this.nextWord();
    },

    // 计算result值的方法
    calculateResult(isCorrect) {
      // 可以根据用户的具体操作来计算更精确的result值
      // 0=不认识, 1=有点模糊, 2=认识, 3=很熟悉
      return isCorrect ? 3 : 0;
    },


    // 完成学习会话
    completeSession() {
      this.studyTime = Date.now() - this.startTime;
      this.currentStep = "complete";

      // 发送完成事件
      this.$emit("session-complete", {
        mode: this.mode,
        totalWords: this.answeredCount,
        correctCount: this.correctCount,
        accuracy:
          this.answeredCount > 0
            ? (this.correctCount / this.answeredCount) * 100
            : 0,
        studyTime: this.studyTime,
      });
    },

    // 重新开始会话
    restartSession() {
      this.initDialog();
    },

    // 切换背景音乐
    toggleBackgroundMusic() {
      this.backgroundMusicPlaying = !this.backgroundMusicPlaying;

      if (this.backgroundMusicPlaying) {
        this.playBackgroundMusic();
      } else {
        this.stopBackgroundMusic();
      }
    },

    // 播放背景音乐
    playBackgroundMusic() {
      try {
        if (!this.backgroundMusic) {
          // 创建音频对象
          this.backgroundMusic = new Audio();

          // 尝试加载音频文件
          try {
            this.backgroundMusic.src = require("@/assets/media/bgm_audio.mp3");
          } catch (requireError) {
            // 如果文件不存在，使用静默音频或提示用户
            console.warn("背景音乐文件不存在:", requireError);
            this.$message.warning(
              "请将背景音乐文件 audio.mp3 放置在 src/assets/media/ 目录下"
            );
            this.backgroundMusicPlaying = false;
            return;
          }

          this.backgroundMusic.loop = true; // 循环播放
          this.backgroundMusic.volume = 0.3; // 设置音量为30%

          // 添加错误处理
          this.backgroundMusic.addEventListener("error", (e) => {
            console.error("音频加载失败:", e);
            this.$message.error("背景音乐加载失败，请检查音频文件");
            this.backgroundMusicPlaying = false;
          });

          // 添加加载完成事件
          this.backgroundMusic.addEventListener("canplay", () => {
            console.log("背景音乐加载完成");
          });
        }

        // 播放音乐
        const playPromise = this.backgroundMusic.play();

        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              this.backgroundMusicPlaying = true;
              // 移除弹窗提示
              // this.$message.success("背景音乐已开启");
            })
            .catch(() => {
              console.error("播放失败");
              this.$message.warning(
                "播放失败，某些浏览器需要用户先进行交互操作"
              );
              this.backgroundMusicPlaying = false;
            });
        }
      } catch (error) {
        console.error("播放背景音乐失败:", error);
        this.$message.warning(
          "背景音乐功能暂不可用，请确保 audio.mp3 文件存在"
        );
        this.backgroundMusicPlaying = false;
      }
    },

    // 停止背景音乐
    stopBackgroundMusic() {
      if (this.backgroundMusic) {
        this.backgroundMusic.pause();
        this.backgroundMusic.currentTime = 0;
      }
      this.backgroundMusicPlaying = false;
      // 移除弹窗提示
      // this.$message.info("背景音乐已关闭");
    },

    // 播放错误提示音
    playErrorSound() {
      try {
        if (!this.errorAudio) {
          try {
            this.errorAudio = new Audio();
            this.errorAudio.src = require("@/assets/media/error_audio.mp3");
            this.errorAudio.volume = 0.5; // 设置音量为50%

            // 添加错误处理
            this.errorAudio.addEventListener("error", () => {
              console.warn("错误提示音加载失败");
            });
          } catch (requireError) {
            // 如果文件不存在，使用浏览器默认音效或静默处理
            console.warn("错误提示音文件不存在:", requireError);
            return;
          }
        }

        // 重置播放位置并播放
        this.errorAudio.currentTime = 0;
        const playPromise = this.errorAudio.play();

        if (playPromise !== undefined) {
          playPromise.catch((error) => {
            console.warn("播放错误提示音失败:", error);
          });
        }
      } catch (error) {
        console.warn("错误提示音功能异常:", error);
      }
    },

    // 播放成功音效
    playSuccessSound() {
      try {
        // 可以使用Web Audio API生成简单的提示音，或者播放成功音效文件
        // 这里先用简单的方式，后续可以添加专门的成功音效文件
        const audioContext = new (window.AudioContext ||
          window.webkitAudioContext)();
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();

        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);

        oscillator.frequency.value = 800; // 频率
        gainNode.gain.value = 0.1; // 音量

        oscillator.start();
        oscillator.stop(audioContext.currentTime + 0.2); // 播放0.2秒
      } catch (error) {
        console.warn("播放成功音效失败:", error);
      }
    },

    // 关闭对话框
    closeDialog() {
      this.visible = false;
    },

    // 对话框关闭回调
    handleClosed() {
      // 停止背景音乐
      this.stopBackgroundMusic();
      this.$emit("closed");
    },

    // 三阶段学习方法
    getPhaseText() {
      const phaseTexts = {
        'phase1-start': '准备开始第一阶段',
        'phase1': '第一阶段：英文→中文',
        'phase2-start': '准备开始第二阶段',
        'phase2': '第二阶段：中文→英文',
        'phase3-start': '准备开始第三阶段',
        'phase3': '第三阶段：拼写测试',
        'summary': '学习总结'
      };
      return phaseTexts[this.currentPhase] || '';
    },

    // 开始第一阶段
    startPhase1() {
      this.currentPhase = 'phase1';
      this.currentIndex = 0;
      this.currentWord = this.wordsList[0];
      this.startTime = Date.now();
      
      // 自动播放背景音乐
      // this.playBackgroundMusic();

      // 自动播放两遍读音
      this.autoPlayWordTwice();
    },

    // 开始第二阶段
    startPhase2() {
      this.currentPhase = 'phase2';
      this.currentIndex = 0;
      this.currentWord = this.wordsList[0];
      
      // 自动播放背景音乐
      // this.playBackgroundMusic();

      // 自动播放两遍读音
      this.autoPlayWordTwice();
    },

    // 开始第三阶段
    startPhase3() {
      this.currentPhase = 'phase3';
      this.currentIndex = 0;
      this.currentWord = this.wordsList[0];
      
      // 自动播放背景音乐
      // this.playBackgroundMusic();

      // 自动播放两遍读音
      this.autoPlayWordTwice();
      
      // 自动聚焦到输入框
      this.$nextTick(() => {
        if (this.$refs.spellingInput) {
          this.$refs.spellingInput.focus();
        }
      });
    },

    // 标记为记住
    markAsKnown() {
      this.showMeaning = true;
      this.isRemembered = true;
      
      // P0和P1-4修复：使用Map存储结果，支持任意长度的单词列表
      const wordId = this.currentWord.id || this.currentWord.word_id || this.currentWord.word;
      if (wordId) {
        if (this.currentPhase === 'phase1') {
          this.phase1ResultMap.set(wordId, true);
        } else if (this.currentPhase === 'phase2') {
          this.phase2ResultMap.set(wordId, true);
        }
      }
      
      // 记住的单词直接跳过，不需要重复学习
      setTimeout(() => {
        this.nextWord();
      }, 2000);
    },

    // 标记为没记住
    markAsUnknown() {
      this.showMeaning = true;
      this.isRemembered = false;
      
      // P0和P1-4修复：使用Map存储结果，支持任意长度的单词列表
      const wordId = this.currentWord.id || this.currentWord.word_id || this.currentWord.word;
      if (wordId) {
        if (this.currentPhase === 'phase1') {
          this.phase1ResultMap.set(wordId, false);
        } else if (this.currentPhase === 'phase2') {
          this.phase2ResultMap.set(wordId, false);
        }
      }
      
      // 没记住的单词需要重复学习，延迟更长时间
      setTimeout(() => {
        this.nextWord();
      }, 3000);
    },

    // 检查拼写
    checkSpelling() {
      if (!this.userInput.trim()) return;
      
      const userAnswer = this.userInput.trim().toLowerCase();
      const correctAnswer = (this.currentWord.word || this.currentWord.english_word).toLowerCase();
      
      this.isCorrect = userAnswer === correctAnswer;
      this.showAnswer = true;
      
      // P0和P1-4修复：使用Map存储结果
      const wordId = this.currentWord.id || this.currentWord.word_id || this.currentWord.word;
      if (wordId) {
        this.phase3ResultMap.set(wordId, this.isCorrect);
      }
      
      if (this.isCorrect) {
        // 拼写正确，重置重试次数，3秒后进入下一个单词
        const wordKey = this.currentWord.word || this.currentWord.english_word;
        this.spellingRetryCount[wordKey] = 0;
        setTimeout(() => {
          this.nextWord();
        }, 3000);
      } else {
        // P0修复：拼写错误，检查重试次数
        const wordKey = this.currentWord.word || this.currentWord.english_word;
        this.spellingRetryCount[wordKey] = (this.spellingRetryCount[wordKey] || 0) + 1;
        
        if (this.spellingRetryCount[wordKey] >= this.maxSpellingRetries) {
          // 达到最大重试次数，标记为错误并进入下一个单词
          this.$message.warning(`单词 "${wordKey}" 已达到最大重试次数，请继续下一个`);
          setTimeout(() => {
            this.spellingRetryCount[wordKey] = 0;
            this.nextWord();
          }, 3000);
        } else {
          // 还可以重试，5秒后清空输入框
          this.$message.info(`拼写错误，还可以重试 ${this.maxSpellingRetries - this.spellingRetryCount[wordKey]} 次`);
          setTimeout(() => {
            this.userInput = '';
            this.showAnswer = false;
            this.isCorrect = false;
            // 聚焦到输入框
            this.$nextTick(() => {
              if (this.$refs.spellingInput) {
                this.$refs.spellingInput.focus();
              }
            });
          }, 5000);
        }
      }
    },

    // 找到对应的原始单词索引
    // P1-3修复：改进原始单词索引查找，更加健壮
    findOriginalWordIndex(currentWord) {
      if (!currentWord) return -1;
      
      // 优先使用ID匹配（最准确）
      if (currentWord.id) {
        for (let i = 0; i < this.originalWordCount; i++) {
          const word = this.wordsList[i];
          if (word && word.id === currentWord.id) {
            return i;
          }
        }
      }
      
      // 次级使用单词文本匹配
      const currentWordText = (currentWord.word || currentWord.english_word || '').toLowerCase();
      if (currentWordText) {
        for (let i = 0; i < this.originalWordCount; i++) {
          const word = this.wordsList[i];
          if (word) {
            const wordText = (word.word || word.english_word || '').toLowerCase();
            if (wordText === currentWordText) {
              return i;
            }
          }
        }
      }
      
      return -1;
    },

    // 下一个单词
    nextWord() {
      this.showMeaning = false;
      this.userInput = '';
      this.showAnswer = false;
      this.isCorrect = false;
      this.isRemembered = false;
      
      // 检查是否完成了当前阶段的所有单词（包括重新学习的）
      if (this.currentIndex < this.totalWords - 1) {
        this.currentIndex++;
        this.currentWord = this.wordsList[this.currentIndex];
        setTimeout(() => {
          this.autoPlayWordTwice();
        }, 500);
        // 如果是拼写测试阶段，自动聚焦到输入框
        if (this.currentPhase === 'phase3') {
          this.$nextTick(() => {
            if (this.$refs.spellingInput) {
              this.$refs.spellingInput.focus();
            }
          });
        }
      } else {
        // 当前阶段完成，检查是否有没记住的单词需要重新学习
        this.checkPhaseCompletion();
      }
    },

    // 检查阶段完成情况
    checkPhaseCompletion() {
      // 防止重复调用
      if (this.isReviewingUnknownWords) {
        return;
      }
      
      // P1-1修复：检查是否还有没记住的单词需要重新学习（包括重新学习后的单词）
      const currentPhaseUnknownWords = [];
      
      // 检查所有单词，不仅仅是原始单词
      for (let i = 0; i < this.totalWords; i++) {
        const word = this.wordsList[i];
        if (!word) continue;  // 安全检查
        
        let isUnknown = false;
        const wordId = word.id || word.word_id || word.word;
        
        if (this.currentPhase === 'phase1') {
          isUnknown = this.phase1ResultMap.get(wordId) === false;
        } else if (this.currentPhase === 'phase2') {
          isUnknown = this.phase2ResultMap.get(wordId) === false;
        } else if (this.currentPhase === 'phase3') {
          isUnknown = this.phase3ResultMap.get(wordId) === false;
        }
        
        if (isUnknown) {
          currentPhaseUnknownWords.push(word);
        }
      }
      
      // 如果有没记住的单词，在当前阶段重新学习
      if (currentPhaseUnknownWords.length > 0) {
        this.reviewUnknownWordsInCurrentPhase(currentPhaseUnknownWords);
      } else {
        // 没有没记住的单词，进入下一阶段
        this.nextPhase();
      }
    },

    // 在当前阶段重新学习没记住的单词
    reviewUnknownWordsInCurrentPhase(unknownWords) {
      // P0修复：防止重复调用和无限循环
      if (this.isReviewingUnknownWords) {
        console.log('[P0] 检测到重复调用，阻止无限循环');
        return;
      }
      
      // 检查当前阶段的重试次数
      const currentPhaseKey = this.currentPhase.replace('-start', '');
      if (this.phaseRetryCount[currentPhaseKey] >= this.maxPhaseRetries) {
        this.$message.warning(`当前阶段已达到最大重新学习次数(${this.maxPhaseRetries}次)，将进入下一阶段`);
        // 强制进入下一阶段
        this.nextPhase();
        return;
      }
      
      // 设置标志防止重复调用
      this.isReviewingUnknownWords = true;
      this.phaseRetryCount[currentPhaseKey]++;
      
      if (this.currentPhase === 'phase3') {
        this.$message.warning(`拼写测试有 ${unknownWords.length} 个单词错误，第 ${this.phaseRetryCount[currentPhaseKey]} 次重新测试`);
      } else {
        this.$message.info(`当前阶段有 ${unknownWords.length} 个单词没记住，第 ${this.phaseRetryCount[currentPhaseKey]} 次重新学习`);
      }
      
      // 将没记住的单词添加到当前单词列表末尾（只添加一次）
      this.wordsList = this.wordsList.concat(unknownWords);
      this.totalWords = this.wordsList.length;
      
      // 重置当前单词索引，从重新学习的单词开始
      this.currentIndex = this.originalWordCount;
      this.currentWord = this.wordsList[this.currentIndex];
      
      // 如果是拼写测试阶段，自动聚焦到输入框
      if (this.currentPhase === 'phase3') {
        this.$nextTick(() => {
          if (this.$refs.spellingInput) {
            this.$refs.spellingInput.focus();
          }
        });
      }
      
      // 延迟重置标志，确保当前批次完成后才能再次调用
      setTimeout(() => {
        this.isReviewingUnknownWords = false;
      }, 1000);
    },

    // 下一阶段
    nextPhase() {
      if (this.currentPhase === 'phase1') {
        this.currentPhase = 'phase2-start';
        // P0修复：重置单词列表和重试计数器
        this.wordsList = this.wordsList.slice(0, this.originalWordCount);
        this.totalWords = this.originalWordCount;
        this.currentIndex = 0;
        this.currentWord = this.wordsList[this.currentIndex];
        this.phaseRetryCount.phase2 = 0;  // 重置第二阶段重试次数
      } else if (this.currentPhase === 'phase2') {
        this.currentPhase = 'phase3-start';
        // P0修复：重置单词列表和重试计数器
        this.wordsList = this.wordsList.slice(0, this.originalWordCount);
        this.totalWords = this.originalWordCount;
        this.currentIndex = 0;
        this.currentWord = this.wordsList[this.currentIndex];
        this.phaseRetryCount.phase3 = 0;  // 重置第三阶段重试次数
        this.spellingRetryCount = {};     // 重置拼写重试计数器
      } else if (this.currentPhase === 'phase3') {
        this.showSummary();
      }
    },

    // 显示总结
    showSummary() {
      this.currentPhase = 'summary';
      this.studyTime = Math.floor((Date.now() - this.startTime) / 1000); // 秒
      
      // 计算学习结果
      this.calculateResults();
    },

    // 计算学习结果
    calculateResults() {
      this.knownWords = [];
      this.unknownWords = [];
      
      // P1-5修复：使用Map结构来正确获取结果，避免数组越界
      // 只处理原始单词数量
      for (let i = 0; i < this.originalWordCount; i++) {
        const word = this.wordsList[i];
        const wordId = word.id || word.word_id || word.word;
        
        // 从Map中获取三个阶段的结果
        const phase1Result = this.phase1ResultMap.get(wordId);
        const phase2Result = this.phase2ResultMap.get(wordId);
        const phase3Result = this.phase3ResultMap.get(wordId);
        
        // P1-5修复：改进学习质量计算逻辑
        // 逻辑：
        // 1. 如果第三阶段拼写正确 → 掌握
        // 2. 如果第一、二阶段都记住，且第三阶段没有拼错 → 掌握
        // 3. 其他情况 → 未掌握
        let isKnown = false;
        
        if (phase3Result === true) {
          // 拼写正确，说明已掌握
          isKnown = true;
        } else if (phase3Result === false) {
          // 拼写错误，未掌握
          isKnown = false;
        } else if (phase1Result === true && phase2Result === true) {
          // 前两个阶段都记住了，即使拼写没测试，也视为掌握
          isKnown = true;
        }
        
        if (isKnown) {
          this.knownWords.push(word);
        } else {
          this.unknownWords.push(word);
        }
      }
      
      // 如果没记住的单词太多，给出建议
      if (this.unknownWords.length > this.totalWords * 0.5) {
        this.$message.warning(`有 ${this.unknownWords.length} 个单词没记住，建议重新学习！`);
      }
    },

    // 重新学习没记住的单词
    reviewUnknownWords() {
      if (this.unknownWords.length === 0) {
        this.$message.info("没有需要重新学习的单词");
        return;
      }
      
      // 重置为没记住的单词列表
      this.wordsList = this.unknownWords;
      this.totalWords = this.wordsList.length;
      this.originalWordCount = this.wordsList.length;  // P0修复：更新原始单词数量
      this.currentIndex = 0;
      this.currentWord = this.wordsList[0];
      
      // P1-4修复：重置结果Map（而不是数组）
      // 这样可以支持任意长度的单词列表，避免数组越界问题
      this.phase1ResultMap = new Map();
      this.phase2ResultMap = new Map();
      this.phase3ResultMap = new Map();
      
      // P0修复：重置所有重试计数器
      this.phaseRetryCount = {
        phase1: 0,
        phase2: 0,
        phase3: 0
      };
      this.spellingRetryCount = {};
      this.isReviewingUnknownWords = false;
      
      // 重新开始第一阶段
      this.currentPhase = 'phase1-start';
      
      this.$message.success(`开始重新学习 ${this.totalWords} 个没记住的单词`);
    },

    // 完成学习
    async finishLearning() {
      try {
        // P2-3修复：准备学习结果数据 - 使用Map结构正确获取所有单词的结果
        const learningResults = [];
        
        for (let i = 0; i < this.originalWordCount; i++) {
          const word = this.wordsList[i];
          if (!word) continue;  // 安全检查
          
          const wordId = word.id || word.word_id || word.word;
          
          // P2-3修复：使用Map结构获取结果
          const phase1Result = this.phase1ResultMap.get(wordId);
          const phase2Result = this.phase2ResultMap.get(wordId);
          const phase3Result = this.phase3ResultMap.get(wordId);
          
          // P2-3修复：完整的综合判断逻辑
          // 同时考虑已记录和未记录的情况
          let isMastered = false;
          
          if (phase3Result !== undefined) {
            // 如果有拼写结果，使用拼写结果
            isMastered = phase3Result === true;
          } else if (phase1Result === true && phase2Result === true) {
            // 如果没有拼写结果，但前两个阶段都记住了
            isMastered = true;
          }
          
          // 确保word_id是有效的数字类型
          if (word && word.id) {
            learningResults.push({
              word_id: parseInt(word.id),
              is_correct: isMastered ? 1 : 0,
              // P2-3修复：记录详细的学习阶段结果，便于后续分析
              phase1_result: phase1Result === true ? 1 : (phase1Result === false ? 0 : null),
              phase2_result: phase2Result === true ? 1 : (phase2Result === false ? 0 : null),
              phase3_result: phase3Result === true ? 1 : (phase3Result === false ? 0 : null)
            });
          }
        }
        
        console.log('提交的学习结果:', learningResults);
        
        // 提交学习结果 - 使用原有的学习记录提交方式
        const response = await homeApi.submitLearning({
          words: learningResults,
          earned_coins: 0,
          review_type: 'learn'
        });
        
        if (response && response.success) {
          this.$emit('session-complete', {
            mode: 'learn',
            totalWords: this.totalWords,
            correctWords: this.knownWords.length,
            wrongWords: this.unknownWords.length,
            studyTime: this.studyTime,
            accuracy: this.masteryRate
          });
          this.closeDialog();
        } else {
          this.$message.error(response.message || "提交学习结果失败");
        }
      } catch (error) {
        console.error("提交学习结果失败:", error);
        this.$message.error("提交学习结果失败");
      }
    },
  },

  // 组件销毁时清理音频资源
  beforeDestroy() {
    this.stopBackgroundMusic();
    if (this.backgroundMusic) {
      this.backgroundMusic = null;
    }
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio = null;
    }
    if (this.errorAudio) {
      this.errorAudio.pause();
      this.errorAudio = null;
    }
  },
};
</script>

<style scoped>
/* 对话框样式 */
::v-deep .word-review-dialog {
  border-radius: 12px;
}

::v-deep .word-review-dialog .el-dialog__header {
  padding: 0;
  border-bottom: 1px solid #ebeef5;
}

::v-deep .word-review-dialog .el-dialog__body {
  padding: 24px;
  height: 540px;
}

/* 对话框头部 */
.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 12px 12px 0 0;
}

.header-info {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 18px;
  font-weight: 600;
}

.header-info i {
  font-size: 20px;
}

.header-controls {
  display: flex;
  gap: 8px;
}

/* 学习进度 */
.learning-progress {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
}

.accuracy {
  color: #67c23a;
  font-weight: 600;
}

/* 开始内容 */
.start-content {
  text-align: center;
}

.stage-info {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.stage-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 8px;
}

.stage-header i {
  font-size: 24px;
  color: #409eff;
}

.stage-header h3 {
  margin: 0;
  font-size: 20px;
  color: #303133;
}

.review-summary {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 32px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.item-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: #409eff;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
}

.item-icon.warning {
  background: #e6a23c;
}

.item-content .number {
  font-size: 24px;
  font-weight: 700;
  color: #303133;
}

.item-content .label {
  font-size: 14px;
  color: #909399;
}

.start-tips {
  background: #f0f9ff;
  border: 1px solid #d1ecf1;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 32px;
  text-align: left;
}

.start-tips h4 {
  margin: 0 0 12px 0;
  color: #0c5460;
  display: flex;
  align-items: center;
  gap: 8px;
}

.start-tips ul {
  margin: 0;
  padding-left: 20px;
  color: #495057;
}

.start-tips li {
  margin-bottom: 8px;
}

.start-actions {
  margin-top: 24px;
}

/* 学习内容 */
.learning-content {
  height: 100%;
  text-align: center;
}

/* 三阶段学习样式 */
.learning-progress {
  margin-bottom: 24px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 14px;
  color: #606266;
}

.current-phase {
  font-weight: 600;
  color: #409EFF;
}

.phase-start {
  height: 90%;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
}

.start-content h2 {
  font-size: 28px;
  color: #2c3e50;
  margin-bottom: 16px;
}

.start-content p {
  font-size: 16px;
  color: #7f8c8d;
  margin-bottom: 12px;
  line-height: 1.6;
}

.start-content .el-button {
  margin: 8px;
}

.phase-content {
  text-align: center;
}

/* 三阶段学习独立样式 */
.phase-word-card {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  height: 410px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.phase-word-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.phase-title {
  font-size: 18px;
  font-weight: 600;
  color: #2c3e50;
}

.phase-word-content {
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
  position: relative;
}

.phase-word-top{
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}

/* 图片区域 - 绝对定位在左侧红框位置 */
.phase-word-image {
  width: 200px;
  height: 150px;
}

.phase-word-img {
  max-width: 200px;
  max-height: 150px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  object-fit: cover;
}

.phase-word-main {
  margin-bottom: 24px;
  /* width: 63%; */
}

.phase-english-word {
  font-size: 28px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.phase-chinese-meaning {
  font-size: 24px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 8px;
}

.phase-word-phonetic {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 15px;
  margin: 10px 0;
}

.phase-phonetic-item {
  font-size: 16px;
  color: #7f8c8d;
  font-style: italic;
  padding: 4px 10px;
  border: 1px solid #e4e7ed;
  border-radius: 10px;
  background: #f8f9fa;
}

.phase-word-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 20px;
}

.phase-word-actions .el-button[type="success"],.phase-word-actions .el-button.el-button--success{
  background:linear-gradient(135deg,#34d399,#059669);
  border:0;
}

.phase-word-actions .el-button.el-button--warning{
  background:linear-gradient(135deg,#fbbf24,#f59e0b);
  border:0;
}

.phase-word-actions .el-button.el-button--info{
  background:linear-gradient(135deg,#64748b,#475569);
  border:0;
  min-width:84px;
  height:40px;
  font-size:14px;
}

.phase-word-actions .el-button:active{
  transform:translateY(0);
  filter:brightness(.95);
} 

.phase-spelling-input {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 20px;
}

.phase-spelling-input .el-input {
  width: 300px;
}

.phase-word-meaning {
  background: white;
  border-radius: 8px;
  padding: 16px;
  border-left: 4px solid #409EFF;
}

.phase-correct {
  color: #67C23A;
}

.phase-incorrect {
  color: #F56C6C;
}

.learning-summary {
  text-align: center;
  padding: 40px 20px;
}

.summary-header h3 {
  font-size: 24px;
  color: #2c3e50;
  margin-bottom: 8px;
}

.summary-header p {
  color: #7f8c8d;
  margin-bottom: 32px;
}

.summary-stats {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 32px;
}

.stat-item {
  text-align: center;
}

.stat-label {
  display: block;
  font-size: 14px;
  color: #7f8c8d;
  margin-bottom: 4px;
}

.stat-value {
  display: block;
  font-size: 24px;
  font-weight: 600;
  color: #409EFF;
}

.stat-value.known {
  color: #67C23A;
}

.stat-value.unknown {
  color: #F56C6C;
}

.stat-value.excellent {
  color: #67C23A;
  font-weight: 700;
}

.stat-value.good {
  color: #E6A23C;
  font-weight: 600;
}

.stat-value.poor {
  color: #F56C6C;
  font-weight: 700;
}

.learning-quality {
  margin-bottom: 24px;
}

.quality-warning {
  background: #fef0f0;
  border: 1px solid #F56C6C;
  border-radius: 8px;
  padding: 12px 16px;
  color: #F56C6C;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.quality-warning i {
  font-size: 16px;
}

.summary-actions {
  display: flex;
  gap: 16px;
  justify-content: center;
}

.word-display-area {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  padding: 40px;
  margin-bottom: 24px;
  color: white;
}

.word-content {
  display: grid;
  grid-template-columns: 200px 1fr 200px;
  align-items: center;
  max-width: 600px;
  margin: 0 auto;
}

/* 单词图片样式 - 固定在左侧 */
.word-image {
  grid-column: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.word-img {
  max-width: 200px;
  max-height: 150px;
  width: auto;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  object-fit: cover;
}

/* 单词文本样式 - 固定在中间 */
.word-main {
  grid-column: 2;
  text-align: center;
  min-width: 0;
}

.word-main .word-text {
  font-size: 48px;
  font-weight: 700;
  margin: 0 0 16px 0;
  letter-spacing: 2px;
}

.word-phonetic {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 20px;
}

.phonetic-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
}

.phonetic-item label {
  font-weight: 600;
}

.word-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
}

/* 含义区域 */
.meaning-area {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 24px;
  text-align: left;
}

.meaning-content h4,
.meaning-content h5 {
  color: #303133;
  margin: 0 0 8px 0;
}

.meaning-text {
  font-size: 18px;
  color: #606266;
  margin: 0 0 20px 0;
  font-weight: 600;
}

.example-sentence {
  border-top: 1px solid #ebeef5;
  padding-top: 16px;
}

.sentence-en {
  font-style: italic;
  color: #409eff;
  margin: 0 0 8px 0;
}

.sentence-cn {
  color: #909399;
  margin: 0;
}

/* 回答按钮 */
.answer-buttons {
  display: flex;
  justify-content: center;
  gap: 20px;
}

/* 完成内容 */
.complete-content {
  text-align: center;
}

.complete-icon {
  font-size: 80px;
  color: #67c23a;
  margin-bottom: 20px;
}

.complete-content h3 {
  font-size: 24px;
  color: #303133;
  margin: 0 0 32px 0;
}

.complete-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-item {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #409eff;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 4px;
}

.complete-actions {
  display: flex;
  justify-content: center;
  gap: 16px;
}

/* 响应式设计 */
/* 平板适配：600px - 1024px */
@media (min-width: 600px) and (max-width: 1024px) {
  ::v-deep .word-review-dialog .el-dialog__body {
    min-height: 540px;
    height: auto;
  }
  
  .phase-word-card{
    min-height: 410px;
    height: auto;
  }

  .phase-start {
    min-height: 410px;
  }

  .word-display-area {
    padding: 24px;
  }

  .word-content {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;
    gap: 20px;
    text-align: center;
  }

  .word-image {
    grid-column: 1;
    grid-row: 1;
  }

  .word-main {
    grid-column: 1;
    grid-row: 2;
    text-align: center;
  }

  .word-main .word-text {
    font-size: 36px;
  }

  .word-img {
    max-width: 150px;
    max-height: 120px;
  }

  .word-phonetic {
    flex-direction: column;
    gap: 12px;
  }

  .review-summary {
    flex-direction: column;
    align-items: center;
    gap: 20px;
  }

  .answer-buttons {
    flex-direction: column;
    align-items: center;
  }

  .complete-stats {
    grid-template-columns: repeat(2, 1fr);
  }

  /* 三阶段学习响应式 */
  .summary-stats {
    flex-direction: column;
    gap: 20px;
  }
  
  .summary-actions {
    flex-direction: column;
  }
  
  .phase-spelling-input {
    flex-direction: column;
  }
  
  .phase-spelling-input .el-input {
    width: 100%;
  }
  
  /* 图片和单词布局响应式 */
  .phase-word-image {
    position: static;
    transform: none;
    width: auto;
    height: auto;
    margin-bottom: 20px;
  }

  .phase-word-img {
    max-width: 150px;
    max-height: 120px;
  }
}
</style>

<!-- 非 scoped 样式：作用于被挂载到 body 的 el-dialog 元素 -->
<style>
/* Element UI 的 el-dialog 默认会 append 到 body，scoped 样式无法穿透。
   在此使用全局样式确保限制最大宽度 */
.word-review-dialog,
.el-dialog__wrapper.word-review-dialog,
.word-review-dialog .el-dialog,
.word-review-dialog.el-dialog__wrapper {
  /* 如果使用 custom-class="word-review-dialog"，Element 会把类添加到 el-dialog__wrapper 上 */
  max-width: 800px !important;
  width: 65% !important;
}
</style>